import type { ComponentType } from 'react';

export interface MigratedRoute {
  /** Path de topo do React Router (pode conter :param). Ex: '/config-bi-multiconta/:id' */
  path: string;
  /**
   * Shell React que hospeda o leaf desta rota. Omitido = 'default' (LayoutShell,
   * com navbar/sidebar/footer) — preserva o comportamento das rotas existentes
   * sem exigir que cada uma declare o campo.
   * - 'chromeless': só a folha, sem chrome, sem RequireAuth e sem skin/DSS no body
   *   — para tela pública acessada sem sessão (ex.: softphone).
   * - 'bare': só a folha, sem chrome, mas sob RequireAuth e com skin/DSS aplicados
   *   no body — para tela autenticada que já traz o próprio enquadramento (ex.:
   *   wizard). Sem as classes de body a folha perde os estilos do novoLayout.
   * - 'branded': só a logo da conta numa faixa lateral, sem navbar/menu — para
   *   telas pré-tenant do login (ex.: seleção de conta), evitando os hooks de
   *   sessão/tenant que o LayoutShell dispara.
   */
  shell?: 'default' | 'chromeless' | 'branded' | 'bare';
  /**
   * Página React do módulo. Ausente = rota servida pela folha Angular em qualquer estado
   * de flag, sem gate.
   */
  page?: ComponentType;
  /**
   * Slug do módulo quando o primeiro segmento do path não é o módulo de domínio
   * (ex.: '/pages/softphone'). Exceção declarada à vista de quem lê o registro.
   */
  flagSlug?: string;
}

/** Slugs que nenhum módulo pode produzir: 'pages' é prefixo técnico e 'kill-switch' é a chave global. */
export const RESERVED_MODULE_SLUGS: readonly string[] = ['pages', 'kill-switch'];

/** Slug do módulo da rota: `flagSlug` declarado ou o primeiro segmento do path. */
export function moduleSlug(route: MigratedRoute): string {
  if (route.flagSlug !== undefined) {
    return route.flagSlug;
  }
  const [first] = route.path.split('/').filter(Boolean);
  // '/' não é módulo; devolver um slug sintético faria nascer uma flag que ninguém criou.
  if (!first) {
    throw new Error(`Rota sem segmento não pertence a módulo: ${route.path}`);
  }
  return first;
}

/** Chave da flag de migração do módulo da rota. */
export function migrationFlagKey(route: MigratedRoute): string {
  return `react-ui-${moduleSlug(route)}`;
}

// Registro único dos módulos cuja rota está sob posse do React e é servida via leaf
// (UI Angular embutida, sem chrome). Cada novo módulo migrado entra aqui.
export const MIGRATED_ROUTES: MigratedRoute[] = [
  { path: '/' },
  { path: '/versao-app-loja/list-versao-app-loja' },
  { path: '/gestao-pre-cadastros/list-pre-cadastros' },
  { path: '/timeline-eventos' },
  { path: '/configmodulos' },
  { path: '/migracao' },
  { path: '/home' },
  { path: '/contrato/list-contrato' },
  { path: '/config-bi-multiconta/:id' },
  { path: '/log-webhook/list-log-webhook' },
  { path: '/log-webhook/list-log-webhook/visualizar-log-webhook/:id' },
  { path: '/log-consulta-erro' },
  { path: '/log-consulta-erro/detalhes/:idLog' },
  { path: '/configuracoes/list-configuracoes' },
  { path: '/configuracoes/list-configuracoes-conta/:id' },
  { path: '/evento/list-psim-eventos/:id' },
  { path: '/portaria-funcionario/list-portaria-funcionario' },
  { path: '/portaria-funcionario/area-portaria-funcionario' },
  { path: '/pages/softphone', shell: 'chromeless', flagSlug: 'softphone' },
  { path: '/sub-integrador/list-sub-integrador' },
  { path: '/sub-integrador/list-sub-integrador/add-sub-integrador' },
  { path: '/sub-integrador/list-sub-integrador/add-sub-integrador/:id' },
  { path: '/perfil-acesso/list-perfil-acesso' },
  { path: '/perfil-acesso/list-perfil-acesso/add-perfil-acesso' },
  { path: '/perfil-acesso/list-perfil-acesso/add-perfil-acesso/:paid' },
  { path: '/horarios/list-horarios' },
  { path: '/horarios/list-horarios/add-horarios' },
  { path: '/horarios/list-horarios/add-horarios/:nhid' },
  { path: '/inquilino/list-inquilino' },
  { path: '/inquilino/list-inquilino/add-inquilino' },
  { path: '/inquilino/list-inquilino/add-inquilino/:id' },
  { path: '/inquilino/list-inquilino/visualizar-inquilino/:id' },
  { path: '/estacionamento/list-estacionamento' },
  { path: '/estacionamento/list-estacionamento/add-estacionamento' },
  { path: '/estacionamento/list-estacionamento/add-estacionamento/:eid' },
  { path: '/estacionamento/list-estacionamento/add-parking-integration' },
  { path: '/vaga/list-vaga/:eid' },
  { path: '/vaga/list-vaga/:eid/add-vaga' },
  { path: '/vaga/list-vaga/:eid/add-vaga/:vid' },
  { path: '/vaga/list-vaga/:eid/vaga-cadastro-automatico/:eid' },
  { path: '/fornecedor/list-fornecedor' },
  { path: '/fornecedor/list-fornecedor/add-fornecedor' },
  { path: '/fornecedor/list-fornecedor/add-fornecedor/:id' },
  { path: '/fornecedor/list-fornecedor/add-fornecedor/:id/:checkId' },
  { path: '/documento/list-documentos' },
  { path: '/documento/list-documentos/add-documento' },
  { path: '/documento/list-documentos/add-documento/:id' },
  { path: '/documento/list-documentos/view-documento/:id' },
  { path: '/categoria/list-categoria' },
  { path: '/categoria/list-categoria/add-categoria' },
  { path: '/categoria/list-categoria/add-categoria/:id' },
  { path: '/credencial/list-credenciais' },
  { path: '/credencial/list-credenciais/add-credenciais' },
  { path: '/credencial/list-credenciais/add-credenciais/:cid' },
  { path: '/credencial/vincular-credencial' },
  { path: '/cftv/list-cftv' },
  { path: '/cftv/list-cftv/add-cftv' },
  { path: '/cftv/list-cftv/add-cftv/:id' },
  { path: '/manutencao-preventiva/list-manutencao-preventiva' },
  { path: '/manutencao-preventiva/list-manutencao-preventiva/add-manutencao-preventiva' },
  {
    path: '/manutencao-preventiva/list-manutencao-preventiva/add-manutencao-preventiva/:id/:checkId',
  },
  // A listagem é a filha '' do próprio prefixo, sem wrapper 'list-*'. O menu aponta
  // para '/zelador-ia/' com barra final, que o React Router, o item ativo da sidebar e o
  // Location da folha normalizam para '/zelador-ia'.
  { path: '/zelador-ia' },
  { path: '/zelador-ia/add-dispositivo' },
  { path: '/zelador-ia/add-dispositivo/:id' },
  { path: '/zelador-ia/dispositivo/:id' },
  { path: '/portaria/list-portaria' },
  { path: '/portaria/list-portaria/add-portaria' },
  { path: '/portaria/list-portaria/add-portaria/:id' },
  { path: '/log-notificacao/list-log-notificacao' },
  { path: '/log-notificacao/list-log-notificacao/visualizar-log-notificacao/:id/:tipo' },
  { path: '/log-notificacao/list-log-dispositivos' },
  { path: '/log-notificacao/list-log-dispositivos/visualizar-log-notificacao/:id/:tipo' },
  // Branded: select-conta é uma tela pré-tenant do fluxo de login. Exibe só a logo
  // da conta, sem o chrome (sidebar/navbar) — evitando as chamadas tenant-scoped que
  // eles disparam no mount (ex.: useDocumentoLeiDados), que sem tenant redirecionariam
  // para o login.
  { path: '/conta/select-conta', shell: 'branded' },
  { path: '/conta/manage-conta' },
  { path: '/conta/migrar-conta' },
  { path: '/conta/list-conta' },
  { path: '/notificacao-tipo-evento/list-notificacao-tipo-evento/:id' },
  { path: '/notificacao-tipo-evento/list-notificacao-tipo-evento/:id/add-notificacao-tipo-evento' },
  {
    path: '/notificacao-tipo-evento/list-notificacao-tipo-evento/:id/add-notificacao-tipo-evento/:nid',
  },
  { path: '/config-cameras/list-configcameras/:id' },
  { path: '/config-cameras/list-configcameras/:id/add-configcameras' },
  { path: '/config-cameras/list-configcameras/:id/add-configcameras/:cid' },
  { path: '/cameras/list-cameras/:id' },
  { path: '/cameras/list-cameras/:id/configurar-list-cameras' },
  { path: '/cameras/lpr-cameras/:id' },
  { path: '/cameras/list-cameras-menu' },
  { path: '/cameras/list-cameras-menu/configurar-list-cameras' },
  { path: '/config-scond/list-key/:id' },
  { path: '/config-scond/list-key/:id/configuracoes-porta/:pid' },
  { path: '/config-scond/list-key/:id/configuracoes-zona-porta/:id' },
  { path: '/config-scond/list-config-zonas/:id' },
  { path: '/config-scond/list-config-zonas/:id/configuracoes-zona-porta/:id' },
  { path: '/agendamento/list-agendamento' },
  { path: '/agendamento/list-agendamento/:aid' },
  { path: '/agendamento/list-agendamento/confirmar-agendamento-component/novo/:aid/:dtagenda' },
  { path: '/agendamento/list-agendamento/confirmar-agendamento-component/:aid/:eid' },
  { path: '/agendamento/list-agendamento/confirmar-agendamento-component/aprovar/:aid/:eid' },
  { path: '/lista-convidados/list-lista-convidados' },
  { path: '/lista-convidados/list-lista-convidados/add-lista-convidados' },
  { path: '/lista-convidados/list-lista-convidados/add-lista-convidados/:id' },
  { path: '/area-comum/list-area-comum' },
  { path: '/area-comum/list-area-comum/add-area-comum' },
  { path: '/area-comum/list-area-comum/add-area-comum/:aid' },
  { path: '/area-comum/list-area-comum/step-add-area-comum' },
  { path: '/area-comum/list-area-comum/step-add-area-comum/:aid' },
  { path: '/lgpd/list-lgpd-termos-uso' },
  { path: '/lgpd/list-lgpd-termos-uso/add-lgpd/:tipo' },
  { path: '/lgpd/list-lgpd-termos-uso/add-lgpd/:tipo/:id' },
  { path: '/lgpd/list-lgpd-politica-privacidade' },
  { path: '/lgpd/list-lgpd-politica-privacidade/add-lgpd/:tipo' },
  { path: '/lgpd/list-lgpd-politica-privacidade/add-lgpd/:tipo/:id' },
  { path: '/grupo-pessoa/list-grupo-pessoa' },
  { path: '/grupo-pessoa/list-grupo-pessoa/add-grupo-pessoa' },
  { path: '/grupo-pessoa/list-grupo-pessoa/add-grupo-pessoa/:id' },
  // Barra final literal: espelha o segmento 'add-permissao-pessoa/' declarado em
  // GrupoPessoaRoutingModule, distinto da rota com :id abaixo.
  { path: '/grupo-pessoa/list-grupo-pessoa/add-permissao-pessoa/' },
  { path: '/grupo-pessoa/list-grupo-pessoa/add-permissao-pessoa/:id' },
  { path: '/grupo-pessoa/list-grupo-pessoa/add-permissao-pessoa/:id/:isSigma' },
  { path: '/chamado/list-categoria-chamado' },
  { path: '/chamado/list-categoria-chamado/add-categoria-chamado' },
  { path: '/chamado/list-categoria-chamado/add-categoria-chamado/:id' },
  { path: '/chamado/list-chamado' },
  { path: '/chamado/list-chamado/add-chamado' },
  { path: '/chamado/list-chamado/add-chamado/:rid/:checkId' },
  { path: '/enquete/list-enquetes' },
  { path: '/enquete/list-enquetes/add-enquete' },
  { path: '/enquete/list-enquetes/add-enquete/:id' },
  { path: '/enquete/list-enquetes/visualizar-enquete' },
  { path: '/enquete/list-enquetes/visualizar-enquete/:id' },
  { path: '/enquete/list-enquetes/resultado-enquete' },
  { path: '/enquete/list-enquetes/resultado-enquete/:id' },
  { path: '/voip/list-dispositivo-sip' },
  { path: '/voip/list-dispositivo-sip/add-dispositivo-sip' },
  { path: '/voip/list-dispositivo-sip/add-dispositivo-sip/:id' },
  { path: '/voip/list-tronco' },
  { path: '/voip/list-tronco/add-tronco' },
  { path: '/voip/list-tronco/add-tronco/:id' },
  { path: '/voip/config-voip' },
  { path: '/recado/list-recado' },
  { path: '/recado/list-recado/add-recado' },
  { path: '/recado/list-recado/add-recado/:rid' },
  // 'view-recado' (singular) e 'views-recado' (plural) são telas e componentes
  // distintos — não confundir os dois pares.
  { path: '/recado/list-recado/view-recado' },
  { path: '/recado/list-recado/view-recado/:rid' },
  { path: '/recado/list-recado/views-recado' },
  { path: '/recado/list-recado/views-recado/:rid' },
  { path: '/assembleia/list-assembleia' },
  // O segmento 'assembleia' aparece três vezes no path das quatro rotas abaixo:
  // prefixo do módulo + wrapper 'list-assembleia' + primeiro segmento da filha
  // ('assembleia/:id/...'). Nenhuma das três ocorrências deve ser colapsada.
  { path: '/assembleia/list-assembleia/assembleia/:id/pauta/:pautaId' },
  { path: '/assembleia/list-assembleia/assembleia/:id/pauta' },
  { path: '/assembleia/list-assembleia/assembleia/:id/votos' },
  { path: '/assembleia/list-assembleia/assembleia/:id/graficos' },
  { path: '/assembleia/list-assembleia/step-assembleia' },
  { path: '/assembleia/list-assembleia/step-assembleia/:id' },
  { path: '/correspondencia/list-correspondencias' },
  { path: '/correspondencia/list-correspondencias/add-correspondencias' },
  { path: '/correspondencia/list-correspondencias/edit-correspondencias/:cid' },
  // 'entregar-component' sem :cid monta AddCorrespondenciaComponent, o mesmo
  // componente da entrada acima — não entregarComponent. Só a entrada com
  // :cid, abaixo, monta entregarComponent. Não colapsar as duas em uma só.
  { path: '/correspondencia/list-correspondencias/entregar-component' },
  { path: '/correspondencia/list-correspondencias/entregar-component/:cid' },
  { path: '/correspondencia/list-correspondencias/visualizar-correspondencias' },
  { path: '/correspondencia/list-correspondencias/visualizar-correspondencias/:cid' },
  { path: '/correspondencia/list-correspondencias-unidade' },
  { path: '/pessoa/list-pessoa' },
  { path: '/pessoa/list-pessoa/add-pessoa' },
  // A rota com :pid monta o MESMO componente da entrada acima
  // (ContainerAddPessoaComponent), em modo editar em vez de criar.
  { path: '/pessoa/list-pessoa/add-pessoa/:pid' },
  // As quatro rotas abaixo montam componentes declarados por MeusDadosModule,
  // não por PessoaModule — mesmo padrão do precedente já existente de
  // UnidadeRoutingModule roteando ContainerAddPessoaComponent. Parâmetro é
  // :pid, não :id.
  { path: '/pessoa/list-pessoa/pessoas/meus-dados/:pid' },
  { path: '/pessoa/list-pessoa/pessoas/meus-dados/residents/:pid' },
  { path: '/pessoa/list-pessoa/pessoas/meus-dados/contact_address/:pid' },
  { path: '/pessoa/list-pessoa/pessoas/meus-dados/access_data/:pid' },
  { path: '/relatorio/relatorio-chamado' },
  { path: '/relatorio/relatorio-agendamentos' },
  { path: '/relatorio/relatorio-chamadas' },
  // RelatorioLgpdDistribuidorComponent/RelatorioLgpdIntegradorComponent vivem em
  // relatorio-lgpd/distribuidor/ e relatorio-lgpd/integrador/, divergindo do padrão de uma
  // pasta por rota dos demais componentes de relatorio — não afeta o path registrado.
  { path: '/relatorio/relatorio-lgpd-distribuidor' },
  { path: '/relatorio/relatorio-lgpd-integrador' },
  { path: '/relatorio/relatorio-chamadas-integrador' },
  { path: '/relatorio/relatorio-primeiro-acesso' },
  { path: '/relatorio/relatorio-primeiro-acesso-conta' },
  { path: '/relatorio/relatorio-portaria' },
  { path: '/relatorio/relatorio-previsao-visita' },
  { path: '/relatorio/relatorio-unidades' },
  { path: '/relatorio/relatorio-acionamentos' },
  { path: '/relatorio/relatorio-veiculos' },
  { path: '/log-operacoes/list-log-operacoes' },
  { path: '/log-operacoes/list-log-operacoes/detalhes-divisao/:idRev/:idEntidade/:entidade' },
  { path: '/log-operacoes/list-log-operacoes/detalhes-pessoa/:idRev/:idEntidade/:entidade' },
  { path: '/log-operacoes/list-log-operacoes/detalhes-unidade/:idRev/:idEntidade/:entidade' },
  {
    path: '/log-operacoes/list-log-operacoes/detalhes-previsao-visita/:idRev/:idEntidade/:entidade',
  },
  {
    path: '/log-operacoes/list-log-operacoes/detalhes-notificacao-eventos/:idRev/:idEntidade/:entidade',
  },
  { path: '/log-operacoes/list-log-operacoes/detalhes-key/:idRev/:idEntidade/:entidade' },
  {
    path: '/log-operacoes/list-log-operacoes/detalhes-key-config-portas/:idRev/:idEntidade/:entidade',
  },
  { path: '/log-operacoes/list-log-operacoes/detalhes-chave-virtual/:idRev/:idEntidade/:entidade' },
  {
    path: '/log-operacoes/list-log-operacoes/detalhes-chave-virtual-config-portas/:idRev/:idEntidade/:entidade',
  },
  { path: '/log-operacoes/list-log-operacoes/detalhes-log-operacoes/:idRev/:idEntidade/:entidade' },
  { path: '/log-operacoes/list-log-operacoes/detalhes-mfa/:idRev/:idEntidade/:entidade' },
  { path: '/locker/app-locker' },
  { path: '/locker/app-locker/add-biccateca' },
  { path: '/locker/app-locker/add-biccateca/:cid' },
  { path: '/locker/app-locker/add-locker' },
  { path: '/locker/app-locker/add-locker/:tipo' },
  { path: '/locker/list-biccateca' },
  // 'add-biccateca' aqui e sob 'app-locker' acima são quatro entradas distintas
  // (duas exatas, duas com :cid) que resolvem para o mesmo AddBiccatecaComponent
  // no Angular — reuso entre duas rotas-pai irmãs, sem afetar o mapeamento path → shell.
  { path: '/locker/list-biccateca/add-biccateca' },
  { path: '/locker/list-biccateca/add-biccateca/:cid' },
  { path: '/locker/list-entrega-autonoma' },
  { path: '/locker/list-entrega-autonoma/add-entrega-autonoma' },
  { path: '/locker/list-entrega-autonoma/add-entrega-autonoma/:eid' },
  { path: '/config-psim/configpsim-conexao/:id' },
  // 'configpsim-gerais' (sem :id, sem guard) e 'configpsim-gerais/:id' (com TenantGuard) são
  // rotas irmãs de topo no Angular Router, não pai/filha — resolvem para o mesmo
  // ConfigPSIMGeraisComponent com portões diferentes. 'registrationAccess', mais abaixo, é o
  // terceiro path desse mesmo componente, sob o guard herdado de um parent distinto
  // (step-configpsim/:id). Esse reuso cross-regime de gating vive só do lado Angular; não
  // afeta o mapeamento path -> shell aqui.
  { path: '/config-psim/configpsim-gerais' },
  { path: '/config-psim/configpsim-gerais/:id' },
  { path: '/config-psim/step-configpsim/:id' },
  { path: '/config-psim/step-configpsim/:id/configpsim-avancadas' },
  { path: '/config-psim/step-configpsim/:id/configpsim-avancadas-envio' },
  { path: '/config-psim/step-configpsim/:id/registrationAccess' },
  { path: '/config-psim/step-configpsim/:id/dataReceiving' },
  { path: '/config-psim/step-configpsim/:id/dataSending' },
  { path: '/equipamento/list-equipamento' },
  { path: '/equipamento/list-equipamento/add-equipamento' },
  { path: '/equipamento/list-equipamento/add-equipamento/:cid' },
  { path: '/equipamento/list-equipamento/add-equipamento/:cid/:nid' },
  // 'list-equipamento/:id' (com TenantGuard) declara a MESMA sub-árvore de quatro rotas já
  // declarada acima sob 'list-equipamento' (sem guard) — árvore duplicada no Angular Router,
  // registrada aqui como declarada, sem unificação (Estágio 2).
  { path: '/equipamento/list-equipamento/:id' },
  { path: '/equipamento/list-equipamento/:id/add-equipamento' },
  { path: '/equipamento/list-equipamento/:id/add-equipamento/:cid' },
  { path: '/equipamento/list-equipamento/:id/add-equipamento/:cid/:nid' },
  { path: '/equipamento/porta-divisao-area' },
  { path: '/hub/list-equipamento-hub' },
  { path: '/hub/list-equipamento-hub/add-equipamento-hub' },
  { path: '/hub/list-equipamento-hub/add-equipamento-hub/:cid' },
  { path: '/hub/list-equipamento-hub/add-equipamento-hub/:cid/:nid' },
  // 'list-equipamento-hub/:id' (com TenantGuard) declara a MESMA sub-árvore de quatro rotas
  // já declarada acima sob 'list-equipamento-hub' (sem guard) — árvore duplicada no Angular
  // Router, registrada aqui como declarada, sem unificação (Estágio 2).
  { path: '/hub/list-equipamento-hub/:id' },
  { path: '/hub/list-equipamento-hub/:id/add-equipamento-hub' },
  { path: '/hub/list-equipamento-hub/:id/add-equipamento-hub/:cid' },
  { path: '/hub/list-equipamento-hub/:id/add-equipamento-hub/:cid/:nid' },
  { path: '/hub/porta-hub' },
  // As duas rotas abaixo são filhas-irmãs de '' sob porta-hub, montando componentes
  // declarados por ConfigScondKeyModule, não por HubModule (roteamento cross-module,
  // HubModule não importa ConfigScondKeyModule).
  { path: '/hub/porta-hub/configuracoes-porta/:pid' },
  { path: '/hub/porta-hub/configuracoes-zona-porta/:id' },
  // As três rotas abaixo são rotas-pai wrapper com children de um único elemento (''),
  // sem nenhuma rota irmã sob o mesmo pai.
  { path: '/hub/status-equipamento-hub' },
  { path: '/hub/log-eventos-hub' },
  { path: '/hub/log-sincronizacao-hub' },
  { path: '/unidade/list-unidade' },
  { path: '/unidade/list-unidade/add-unidade' },
  // A rota com :uid monta o MESMO componente da entrada acima
  // (AddUnidadeComponent), em modo editar em vez de criar. No Angular Router,
  // 'add-unidade/:uid' é uma rota-pai componentless (agrupa os filhos abaixo),
  // mas isso é opaco ao registro React, que trata cada path como um literal
  // independente.
  { path: '/unidade/list-unidade/add-unidade/:uid' },
  // As rotas de 'add-pessoa'/'add-pessoa/:pid' abaixo (e as duas equivalentes
  // sob 'vincular-pessoa-unidade') montam ContainerAddPessoaComponent,
  // declarado por PessoaModule, não por UnidadeModule — mesmo padrão de
  // roteamento cross-module já usado por 'pessoa'. O :uid chega a elas por
  // herança de params do(s) pai(s) componentless (mecânica padrão do Angular
  // Router, sem código específico do leaf).
  { path: '/unidade/list-unidade/add-unidade/:uid/add-pessoa' },
  { path: '/unidade/list-unidade/add-unidade/:uid/add-pessoa/:pid' },
  { path: '/unidade/list-unidade/add-unidade/:uid/vincular-pessoa-unidade' },
  { path: '/unidade/list-unidade/add-unidade/:uid/vincular-pessoa-unidade/add-pessoa' },
  { path: '/unidade/list-unidade/add-unidade/:uid/vincular-pessoa-unidade/add-pessoa/:pid' },
  { path: '/unidade/cadastro-automatico/:id' },
  { path: '/unidade/divisoes/:id' },
  // '/integrador' é um redirect de raiz ('' -> 'list-integrador', pathMatch: 'full') em
  // IntegradorRoutingModule, resolvido pelo roteador Angular interno da folha — sem código
  // específico do leaf.
  { path: '/integrador' },
  { path: '/integrador/list-integrador' },
  { path: '/integrador/list-integrador/add-integrador' },
  // A rota com :id monta o MESMO AddIntegradorComponent da entrada acima, em modo editar.
  { path: '/integrador/list-integrador/add-integrador/:id' },
  { path: '/integrador/list-integrador/add-integrador/:id/custom-skin' },
  { path: '/integrador/list-integrador/add-integrador/:id/contrato-integrador' },
  // As duas rotas abaixo (e as duas equivalentes de topo, mais abaixo) montam
  // SmsViewComponent/SmsConfigComponent — a mesma sub-árvore é declarada duas vezes em
  // IntegradorRoutingModule. O :id chega aqui por herança de params através de dois níveis de
  // rota componentless encadeados (add-integrador/:id e o próprio nó sms-view aninhado).
  { path: '/integrador/list-integrador/add-integrador/:id/sms-view' },
  { path: '/integrador/list-integrador/add-integrador/:id/sms-view/sms-config' },
  { path: '/integrador/list-integrador/add-integrador/:id/config-integrador' },
  { path: '/integrador/list-integrador/add-integrador/:id/endereco-integrador' },
  { path: '/integrador/list-integrador/add-integrador/:id/migracao-contrato' },
  // Segundo ponto da árvore sms-view duplicada, sem parâmetro.
  { path: '/integrador/sms-view' },
  { path: '/integrador/sms-view/sms-config' },
  // Monta o mesmo CustomSkinComponent da entrada aninhada acima, sem relação de aninhamento
  // entre as duas rotas.
  { path: '/integrador/custom-skin-integrador' },
  { path: '/integrador/config-autenticacao' },
  { path: '/integrador/add-whatsapp' },
  { path: '/integrador/add-whatsapp/simular-envio' },
  { path: '/integrador/inicio' },
  // As três rotas abaixo são declaradas por um RecadoModule próprio de integrador
  // (loadChildren duplamente aninhado dentro do leaf), com o mesmo nome de classe do
  // RecadoModule de topo (acima, sob '/recado'), sem import cruzado entre os dois pares de
  // arquivos. Diferente do recado de topo, add-recado/:id monta um componente próprio
  // (EditRecadoComponent), não o mesmo componente de criação.
  { path: '/integrador/recado/list-recado' },
  { path: '/integrador/recado/list-recado/add-recado' },
  { path: '/integrador/recado/list-recado/add-recado/:id' },
  { path: '/integrador/shield' },
  { path: '/integrador/add-pre-cadastro' },
  // Rota de topo do MeusDadosModule; a sub-árvore /pessoa/list-pessoa/pessoas/meus-dados/:pid
  // (mais acima) monta componentes do mesmo módulo por outro caminho, sem relação de
  // aninhamento com esta entrada.
  { path: '/meus-dados' },
  { path: '/alterar-senha' },
  { path: '/equipamento-pac/list-equipamento-pac' },
  { path: '/equipamento-pac/list-equipamento-pac/add-equipamento-pac' },
  { path: '/equipamento-pac/list-equipamento-pac/detalhes-equipamento-pac/:id' },
  { path: '/convite-virtual' },
  { path: '/convite-virtual/:id' },
  { path: '/chave-virtual' },
  { path: '/chave-virtual/:id' },
  // Três rotas do WizardRoutingModule sob o prefixo autenticado `wizard`. As
  // variantes públicas (/pages/preCadastro...) montam o mesmo módulo e seguem
  // no AngularHost — só o prefixo autenticado pertence ao React.
  //
  // `/wizard` (primeiro acesso do morador) não leva chrome: o FullLayoutComponent
  // escondia sidebar/navbar nesse fluxo via `getPrimeiroAcesso()`, e o wizard já
  // traz a própria logo no header. Daí 'bare' e não 'chromeless' — a folha ainda
  // precisa das classes de skin/DSS no body para os estilos do novoLayout, e a
  // rota é autenticada. As duas rotas de pré-cadastro mantêm o shell default,
  // que é como aparecem hoje.
  { path: '/wizard', shell: 'bare' },
  { path: '/wizard/preCadastro/:pcid' },
  { path: '/wizard/gestao/preCadastro/:pcid' },
  { path: '/pre-cadastro/list-pre-cadastro-component' },
  // Menu Suporte do integrador, exibido só sem conta selecionada. O detalhe 'buscar/:id'
  // é filho de 'buscar' no mesmo wrapper 'list-modo-debug', não de 'list-modo-debug/:id'.
  { path: '/modo-debug/list-modo-debug' },
  { path: '/modo-debug/list-modo-debug/buscar' },
  { path: '/modo-debug/list-modo-debug/buscar/:id' },
];

/** Deriva o path gêmeo DSS de um path migrado. Ex: '/home' -> '/dss/home'. */
export function toDssPath(path: string): string {
  return `/dss${path}`;
}

/** Deriva o gêmeo `/dss/{path}` de cada rota migrada — paridade sem allowlist manual. */
export function buildDssRoutes(routes: MigratedRoute[]): string[] {
  const seenPaths = new Set<string>();
  for (const route of routes) {
    if (seenPaths.has(route.path)) {
      throw new Error(`MIGRATED_ROUTES contém path duplicado: ${route.path}`);
    }
    seenPaths.add(route.path);
  }

  return routes.map((route) => toDssPath(route.path));
}

export const DSS_ROUTES: string[] = buildDssRoutes(MIGRATED_ROUTES);
